import { useEffect, useState, useCallback } from 'react';

import { useInject } from '@vk-hr-tek/core/ioc';
import { Avatar } from '@vk-hr-tek/ui/Avatar';
import { Box } from '@vk-hr-tek/ui/Box';
import { Typography } from '@vk-hr-tek/ui/Typography';
import { Link } from '@vk-hr-tek/ui/Link';
import { Skeleton } from '@vk-hr-tek/ui/Skeleton';
import { Icon } from '@vk-hr-tek/ui/Icon';
import { HTTP_NOT_FOUND_STATUS } from '@vk-hr-tek/core/http/codes';
import { NoImage } from '@vk-hr-tek/app-ui/NoImage';

import { modules } from '@app/modules';
import { SearchService } from '@app/gen/search';

import { LayoutMapper } from '../../../../mappers';
import { PhotoParams, SearchEmployeeOption } from '../SearchInput.types';
import { useUrlWithPrefix } from '../../../../hooks';
import { AbsenceStatus } from '../../../../segments/AbsenceStatus';

interface SearchOptionEmployeeProps {
  option: SearchEmployeeOption;
  loadPhoto: (params: PhotoParams) => Promise<Blob>;
}

export const SearchOptionEmployee = ({
  option,
  loadPhoto,
}: SearchOptionEmployeeProps) => {
  const [profileUrl, setProfileUrl] = useState(
    `/${modules.personal.basename}/${option.user_id}`,
  );
  const searchService = useInject(SearchService);
  const layoutMapper = useInject(LayoutMapper);
  const [isImgLoading, setIsImgLoading] = useState(true);
  const [showNoImg, setShowNoImg] = useState(false);

  const labels = [option.position_name, option.phone, option.email].filter(
    Boolean,
  );

  useEffect(() => {
    const getRedirectUrl = async () => {
      const result = await searchService.getEmployeePHProfile({
        params: {
          employee_id: option.id,
        },
      });

      if (result.url) {
        setProfileUrl(result.url);
      }
    };

    if (option.ph_profile) {
      getRedirectUrl();
    }
  }, [option.ph_profile, option.id, searchService]);

  const handleLoadPhoto = useCallback(async () => {
    const result = await loadPhoto({
      id: option.id,
    }).then(
      (blob) => {
        setIsImgLoading(false);
        return blob;
      },
      (err) => {
        setIsImgLoading(false);

        if (err.status !== HTTP_NOT_FOUND_STATUS) {
          setShowNoImg(true);
        }

        return err;
      },
    );

    return result;
  }, [loadPhoto, option.id]);

  const urlWithPrefix = useUrlWithPrefix();

  const absences = layoutMapper.formatAbsences(option.absences);

  return (
    <Box width="100%" p="8">
      <Link {...urlWithPrefix(profileUrl)} variant="simple" display="block">
        <Box display="flex" alignItems="center" gap="12">
          <Box
            minHeight="40px"
            minWidth="40px"
            height="40px"
            width="40px"
            display="flex"
            gap="12"
            position="relative"
          >
            {isImgLoading && (
              <Skeleton variant="circle" width="100%" height="100%" />
            )}

            {showNoImg && <NoImage isCircle />}

            {
              <Box display={isImgLoading || showNoImg ? 'none' : 'visible'}>
                <Avatar
                  loadPhoto={handleLoadPhoto}
                  children={`${option.last_name[0]}${option.first_name[0]}`}
                />
              </Box>
            }
            {absences.length > 0 && <AbsenceStatus absence={absences[0]} />}
          </Box>
          <Box
            flex={1}
            display="flex"
            flexDirection="column"
            gap="2"
            maxHeight="60px"
          >
            <Typography
              variant="body3"
              color="text.light.primary"
              maxLinesCount={1}
              wordBreak="break-all"
            >
              {option.last_name} {option.first_name} {option.middle_name}
            </Typography>
            <Box display="flex" gap="4">
              {labels.map((label, index) => (
                <Box key={index} display="flex" alignItems="center" gap="4">
                  <Typography
                    variant="caption"
                    color="text.dark.secondary"
                    maxLinesCount={1}
                    wordBreak="break-all"
                  >
                    {label}
                  </Typography>
                  {index !== labels.length - 1 && (
                    <Box minWidth="16px" minHeight="16px">
                      <Icon
                        name="circle_small_filled"
                        color="text.dark.secondary"
                        size="16"
                      />
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Link>
    </Box>
  );
};
